import '../styles/pdf-pages.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateFestivalEss } from '@/domain/impact-calculate-festival/entities/calculate-festival-ess.entity';
import { selectImpactCalculateFestivalEssForProject } from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectCurrentProjectFestival } from '@/domain/project-festival/project-festival.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfFestivalPage4({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const festival = useAppSelector(selectCurrentProjectFestival);
  const festivalId = festival?.id ?? 0;

  const impactEss = useAppSelector(
    selectImpactCalculateFestivalEssForProject(festivalId),
  ) satisfies CalculateFestivalEss;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!festival) {
    return <h1>{translate('PdfExports.festival-not-found')}</h1>;
  }

  const impactEssScore100 = Math.round(
    (impactEss.score * 100) / impactEss.maxScore,
  );

  return (
    <div id="page4" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{festival.name}</h1>
            {festival.startDate && festival.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="festival-date">
                  {formatProjectDatesTz(
                    festival.startDate || '',
                    festival.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.ess-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEssScore100}</span>/ 100
            </h4>
            <h5>
              {impactEss.score} / {impactEss.maxScore}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="ess"
              scoreLetter={impactEss.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.PARITY_EQUALITY')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactEss.topics.PARITY_EQUALITY.score)}
                  </span>{' '}
                  / {impactEss.topics.PARITY_EQUALITY.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEss.topics.PARITY_EQUALITY.sections,
                    ) as [
                      keyof typeof impactEss.topics.PARITY_EQUALITY.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEss.topics.PARITY_EQUALITY.score}
                  maxScore={impactEss.topics.PARITY_EQUALITY.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.score,
                    )}
                  </span>{' '}
                  / {impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.sections,
                    ) as [
                      keyof typeof impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.score}
                  maxScore={
                    impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.maxScore
                  }
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.score,
                    )}
                  </span>{' '}
                  / {impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.sections,
                    ) as [
                      keyof typeof impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.score}
                  maxScore={
                    impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.maxScore
                  }
                />
              </div>
            </div>
          </div>
          <div
            style={{
              width: 340,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {' '}
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-back-to-env')}
          </a>
          {/* TODO: cette section ne doit être active que si l'utilisateur a activé l'export des réponses */}
          {/* <a href="#page5" className="tag-pill">
            {translate('PdfExports.go-to-question-answers')}
          </a> */}
        </div>
      </div>

      <PdfProjectPager current={4} total={totalPages}></PdfProjectPager>
    </div>
  );
}
